<template>
  <router-link
    class="competition-item"
    :class="{ '--static-width': staticWidth, '--ended': type === 'ended' }"
    :style="customVariebles"
    data-name="competition-item"
    :to="{ name: 'Competition', params: { id: item.objectId } }"
  >
    <img v-if="backgroundImage" class="competition-item__background" :src="backgroundImage" />

    <img v-if="image" class="competition-item__image" :src="image" />

    <u-i-compressed-image
      v-else
      class="competition-item__image"
      :compressed-images="images"
      :media="compressedImagesMedia"
    />

    <div class="competition-item__gradient-top"></div>
    <div class="competition-item__gradient-bottom"></div>

    <div class="competition-item-content">
      <div class="competition-item-content__header">
        <div class="competition-item-content__timer">
          <template v-if="(time.d > 0 || alreadyParticipate || type == 'soon') && type !== 'ended' && time?.s >= 0">
            <Icon v-if="iconName" :name="iconName" />

            <template v-if="time?.d > 0">{{ time?.d }} {{ $t('timer.short.day') }}&nbsp;</template>
            <template v-if="time.h > 1 || (time?.d < 1 && time?.h > 0)"
              >{{ time?.h }} {{ $t('timer.short.hour') }}&nbsp;</template
            >
            <template v-if="time?.d < 1">{{ time?.m }} {{ $t('timer.short.minute') }}&nbsp;</template>
            <template v-if="time?.d < 1 && time.h < 1">{{ time?.s }} {{ $t('timer.short.second') }}</template>
          </template>
        </div>

        <div class="competition-item-content__chips">
          <div
            class="competition-item-content__chip"
            :class="{
              '--running': type === 'running',
              '--soon': type === 'soon',
              '--ended': type === 'ended',
              '--preend': time?.d < 1 && type === 'running',
            }"
          >
            {{ chipText }}
          </div>
          <div v-if="isCryptoTournament" class="competition-item-content__crypto">
            <icon name="doge" />
            <span>{{ $t('cryptoTournament') }}</span>
          </div>
        </div>
      </div>

      <div class="competition-item-content__title">{{ item.name }}</div>

      <div class="competition-item-prices__value" v-html="fund"></div>
      <div class="competition-item-prices__description">{{ $t('fund') }}</div>

      <button
        class="competition-item-content__button"
        size="sm"
        color="gray-night-100"
        type="button"
        :class="{
          '--active': buttonHovered,
          '--not-active': !buttonHovered,
          '--soon': type === 'running' && time?.d < 1 && !alreadyParticipate,
        }"
        data-name="competition-item-action"
        @click.prevent="buttonAction"
        @mouseenter="buttonHovered = true"
        @mouseleave="buttonHovered = false"
      >
        <span class="competition-item-content__button-container">
          <template v-if="type === 'running' && time?.d < 1 && !alreadyParticipate && time?.s >= 0">
            <span class="competition-item-content__button-timer">
              <Icon v-if="type === 'soon'" name="timer" size="12" />
              <Icon v-else-if="type === 'running'" name="timer-active" size="16" />

              <template v-if="time?.d > 0">{{ time?.d }} {{ $t('timer.short.day') }}&nbsp;</template>
              <template v-if="time.h > 1 || (time?.d < 1 && time?.h > 0)"
                >{{ time?.h }} {{ $t('timer.short.hour') }}&nbsp;</template
              >
              <template v-if="time?.d < 1">{{ time?.m }} {{ $t('timer.short.minute') }}&nbsp;</template>
              <template v-if="time?.d < 1 && time.h < 1">{{ time?.s }} {{ $t('timer.short.second') }}</template>
            </span>

            <span class="competition-item-content__button-participate">
              <template v-if="propsContent?.button?.[$i18n.locale]">
                {{ propsContent.button[$i18n.locale] }}
              </template>

              <template v-else>
                {{ $t('participate') }}
              </template>
            </span>
          </template>

          <template v-else-if="type === 'ended'">
            {{ $t('results') }}
          </template>

          <template v-else-if="propsContent?.button?.[$i18n.locale]">
            {{ propsContent.button[$i18n.locale] }}
          </template>

          <template v-else-if="isShownJoinButton">
            <template v-if="alreadyParticipate">
              <Icon name="agree" size="16" />
              {{ $t('youTakePart') }}
            </template>

            <template v-else>
              {{ $t('participate') }}
            </template>
          </template>

          <template v-else>
            {{ $t('details') }}
          </template>
        </span>
      </button>
    </div>
  </router-link>
</template>

<script setup>
import { computed, toRef, ref, onMounted, onBeforeUnmount, watch, defineEmits, inject, defineAsyncComponent } from 'vue'
import UICompressedImage from 'components/ui/UICompressedImage.vue'
import { numberFilter, hexToShortRGB } from 'src/common/functions'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAcceptCompetition } from 'src/hooks/competition'
import { useUserAuthStore } from 'src/stores/user/auth'
import { BASE_URL } from 'src/common/config'

const props = defineProps({
  item: {
    type: [Object],
    required: true,
  },
  staticWidth: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['reload'])

const buttonHovered = ref(false)

const appModal = inject('appModal')
const router = useRouter()
const { t } = useI18n()

const item = toRef(props.item)

const compressedImagesMedia = {
  xs: '(max-width: 767px)',
  sm: '(max-width: 1919px)',
  xl: '(min-width: 1920px)',
}

const images = computed(() => {
  const image = props.item.compressedImage?.webp

  return {
    xs: image?.xs ? image.xs : props.item.imgActive,
  }
})

const fund = computed(() => {
  const value = item.value.customCurrency
    ? numberFilter(item.value.sum, false) + ` <span>${item.value.customCurrency}</span>`
    : numberFilter(item.value.sum, false) + ` <span>${item.value.currency?.code} </span>`
  return value
})

const isShownJoinButton = computed(() => {
  return type.value === 'running' && !item.value.inGame
})
const currentTime = ref(0)
const isEnded = ref(false)

const type = computed(() => {
  if (item.value.ended || isEnded.value) {
    return 'ended'
  } else {
    return +new Date(Date.parse(item.value.dateStart)) >= currentTime.value ? 'soon' : 'running'
  }
})

const chipText = computed(() => {
  if (type.value === 'ended') {
    return t('ended')
  } else if (type.value === 'running') {
    if (time.value?.d < 1) {
      return t('Expires')
    } else {
      return t('active')
    }
  } else {
    return t('Soon')
  }
})

const dateStart = computed(() => {
  return new Date(item.value.dateStart).getTime()
})
const dateEnd = computed(() => {
  return new Date(item.value.dateEnd).getTime()
})

const time = ref({})

function reload() {
  emit('reload')
}

function updateTime() {
  currentTime.value = +new Date()
  let timer

  if (type.value === 'soon') {
    timer = dateStart.value
  } else {
    timer = dateEnd.value
  }

  let toStart = Math.floor((timer - Date.now()) / 1000)

  let days = toStart / 86400

  if (days >= 1) {
    days = Math.floor(days)
    toStart = toStart - days * 86400
  } else {
    days = 0
  }

  let hours = toStart / 3600

  if (hours >= 1) {
    hours = Math.floor(hours)
    toStart = toStart - hours * 3600
  } else {
    hours = 0
  }

  let minutes = toStart / 60

  if (minutes >= 1) {
    minutes = Math.floor(minutes)
    toStart = toStart - minutes * 60
  } else {
    minutes = 0
  }

  let seconds = Math.floor(toStart)

  time.value = {
    d: days,
    h: hours,
    m: minutes,
    s: seconds,
  }
}

const alreadyParticipate = computed(() => {
  return item.value?.score?.active ?? false
})

const isCryptoTournament = computed(() => {
  return item.value.requiredDoge
})

function openRegistration() {
  localStorage.setItem('joinedTournament', item.value.objectId)
  const authModal = defineAsyncComponent(() => import('components/modals/AuthModalNew.vue'))
  const tab = localStorage.getItem('hasAccount') ? 'login' : 'reg'
  appModal.open({ componentName: authModal, props: { tab } })
}

function joinCompetition() {
  if (!useUserAuthStore().getAuth) {
    return openRegistration()
  }
  useAcceptCompetition(item.value).then(() => reload())
}

function buttonAction() {
  if (isShownJoinButton.value && !alreadyParticipate.value) {
    joinCompetition()
  } else {
    router.push({ name: 'Competition', params: { id: item.value.objectId } })
  }
}

let timerInterval

const propsContent = ref(null)

if (item.value?.propsContent && item.value?.propsContent[0]?.props) {
  propsContent.value = JSON.parse(item.value?.propsContent[0]?.props)
}

const iconName = computed(() => {
  if (type.value === 'soon') {
    return 'timer'
  }

  return type.value === 'running' ? 'timer-active' : null
})

const customVariebles = computed(() => {
  const stockColor = hexToShortRGB(window.getComputedStyle(document.body).getPropertyValue('--cas-menu'))

  if (propsContent.value) {
    let colorToCss = hexToShortRGB(propsContent.value.gradientColor)
    if (colorToCss) {
      return `--gradient: ${colorToCss};`
    } else {
      return `--gradient: ${stockColor}`
    }
  }
  return `--gradient: ${stockColor}`
})

const image = computed(() => {
  if (propsContent.value?.image?.compressed_image?.img) {
    return `${BASE_URL}${propsContent.value.image.compressed_image.img}`
  }

  return null
})

const backgroundImage = computed(() => {
  if (propsContent.value?.background?.compressed_image?.img) {
    return `${BASE_URL}${propsContent.value.background.compressed_image.img}`
  }

  return null
})

onMounted(() => {
  updateTime()

  timerInterval = setInterval(() => {
    updateTime()
  }, 1000)

  const joinedTournamentId = +localStorage.getItem('joinedTournament')
  if (joinedTournamentId && joinedTournamentId === item.value.objectId && useUserAuthStore().getAuth) {
    useAcceptCompetition(item.value).then(() => {
      reload()
    })
  }
})

onBeforeUnmount(() => {
  clearInterval(timerInterval)
})

watch(time, () => {
  const values = Object.keys(time.value).map((element) => time.value[element] <= 0)
  if (values.every((item) => item) && type.value === 'running') {
    isEnded.value = true
  }
})
</script>

<style lang="scss">
.competition-item {
  --gradient: 0, 31, 96;

  width: calc((100% - 8px) / 2);
  border-radius: 8px;
  min-height: 211px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;
  color: white;
  text-decoration: none;
  background-color: transparent;
  align-self: flex-start;

  @media (min-width: 768px) {
    width: calc((100% - 24px) / 3);
    min-height: 254px;
  }

  @media (min-width: 1024px) {
    width: calc((100% - 32px) / 3);
    min-height: 385px;
    border-radius: 12px;
  }

  @media (min-width: 1440px) {
    width: calc((100% - 72px) / 4);
  }

  &.--ended {
    > * {
      filter: grayscale(1);
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--cas-menu);
      mix-blend-mode: color;
      opacity: 0.8;
      pointer-events: none;
    }
  }

  &.--static-width {
    @media screen and (min-width: 768px) {
      width: 100%;
    }

    @media screen and (max-width: 768px) {
      width: 200px;
      min-height: 254px;

      .competition-item-content__timer {
        font-weight: 500;
        font-size: 12px;
        line-height: 16px;
      }

      .competition-item-content__title {
        font-size: 16px;
        line-height: 20px;
      }

      .competition-item-prices__value {
        font-weight: 800;
        font-size: 24px;
        line-height: 28px;

        > span {
          font-weight: 800;
          font-size: 14px;
          line-height: 16px;
        }
      }

      .competition-item-prices__description {
        font-weight: 400;
        font-size: 10px;
        line-height: 16px;
      }
    }
  }

  &__background {
    position: absolute;
    display: block;
    top: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    object-fit: cover;
    right: 0;
    bottom: 0;
    border-radius: 9px;

    * {
      border-radius: 9px;
    }
  }

  &__image {
    position: absolute;
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  &__gradient-top {
    position: absolute;
    z-index: 0;
    top: -1px;
    left: -1px;
    right: -1px;
    height: 88px;
    background: radial-gradient(75.56% 100% at 0% 0%, rgba(var(--gradient), 1) 18%, rgba(var(--gradient), 0) 100%);
  }

  &__gradient-bottom {
    position: absolute;
    z-index: 0;
    bottom: 0;
    left: 0;
    right: 0;
    height: 66%;
    background: linear-gradient(
      180deg,
      rgba(var(--gradient), 0) 0%,
      rgba(var(--gradient), 0.72) 28%,
      rgba(var(--gradient), 0.96) 56%
    );
    border-radius: 0 0 8px 8px;
  }

  &-content {
    position: relative;
    z-index: 1;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-end;
    padding: 8px;
    flex-grow: 1;

    @media (min-width: 1024px) {
      padding: 16px;
    }

    &__title {
      font-weight: 500;
      font-size: 14px;
      line-height: 18px;
      color: rgba(255, 255, 255, 0.9);
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;

      @media (min-width: 768px) {
        font-size: 16px;
        line-height: 20px;
      }

      @media (min-width: 1024px) {
        font-size: 18px;
        line-height: 24px;
      }
    }

    &__header {
      position: absolute;
      top: 8px;
      left: 8px;
      right: 8px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &__timer {
      display: flex;
      align-items: center;
      gap: 4px;
      font-weight: 500;
      font-size: 12px;
      line-height: 10px;
      color: var(--cas-light-700);

      > i {
        width: 16px;
        height: 16px;
      }
    }

    &__chips {
      display: flex;
      gap: 2px;
      align-items: center;
    }

    &__chip {
      display: flex;
      padding: 1px 2px;
      gap: 2px;
      background: #865ed4;
      box-shadow: 1px 1px 0px rgba(0, 0, 0, 0.4);
      border-radius: 2px;
      font-style: normal;
      font-weight: 800;
      font-size: 9px;
      line-height: 10px;
      text-transform: uppercase;
      color: #fff;

      @media (min-width: 1024px) {
        font-size: 11px;
        line-height: 12px;
        padding: 2px 4px;
      }

      &.--running {
        background: #219653;
      }

      &.--soon {
        background: #865ed4;
      }

      &.--preend {
        background: #eb509a;
      }

      &.--ended {
        background: var(--cas-menu);
      }
    }

    &__crypto {
      display: flex;
      padding: 1px 2px;
      gap: 2px;
      background: var(--cas-yellow-700);
      box-shadow: 1px 1px 0px rgba(0, 0, 0, 0.4);
      border-radius: 2px;
      color: black;
      height: 12px;
      width: 12px;

      > span {
        display: none;
      }

      > i {
        width: 100%;
        height: 100%;
      }

      @media (min-width: 768px) {
        height: 12px;
        width: 12px;
      }

      @media (min-width: 1024px) {
        width: auto;
        font-weight: 600;
        height: 16px;
        font-size: 11px;
        text-transform: uppercase;
        color: rgba(18, 18, 18, 0.92);
        white-space: nowrap;
        display: flex;
        align-items: center;
        margin-left: 4px;

        > i {
          width: 12px;
          height: 12px;
        }

        > span {
          display: block;
        }

        > * {
          flex-shrink: 0;
        }
      }
    }

    &__button-participate {
      position: absolute;
      left: 0;
      right: 0;
      width: 100%;
      height: 100%;
      bottom: 64px;
      transition: all 0.4s;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &__button-timer {
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      gap: 4px;
      left: 0;
      right: 0;
      width: 100%;
      height: 100%;
      bottom: 0;
      transition: all 0.4s;
    }

    &__button {
      width: 100%;
      margin-top: 8px;
      height: 32px;
      overflow: hidden;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 8px 0;
      gap: 8px;
      background: rgba(255, 255, 255, 0.07);
      border-radius: 5px;
      border: none;
      position: relative;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }

      @media (min-width: 768px) {
        margin-top: 12px;
      }

      @media (min-width: 1024px) {
        height: 40px;
        margin-top: 12px;
      }

      &.--active {
        .competition-item-content__button-participate {
          bottom: 0;
          animation: 0.6s participate-hover ease-in-out;
        }

        .competition-item-content__button-timer {
          bottom: -64px;
        }
      }

      &.--not-active {
        .competition-item-content__button-timer {
          animation: 0.6s timer-hover ease-in-out;
        }
      }

      &.--soon {
        animation: 2s soon ease-in-out infinite;
      }
    }

    &__button-container {
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 500;
      font-size: 14px;
      line-height: 14px;
      color: rgba(255, 255, 255, 0.9);

      @media (min-width: 768px) {
        font-size: 16px;
        line-height: 1px;
      }
    }
  }

  &-prices {
    &__value {
      font-weight: 800;
      color: var(--cas-primary);
      font-size: 16px;
      line-height: 20px;
      margin-top: 8px;

      > span {
        font-weight: 800;
        font-size: 10px;
        line-height: 12px;
      }

      @media (min-width: 768px) {
        margin-top: 12px;
        font-size: 24px;
        line-height: 28px;

        > span {
          font-size: 14px;
          line-height: 16px;
        }
      }

      @media (min-width: 1024px) {
        margin-top: 16px;
        font-size: 32px;
        line-height: 40px;

        > span {
          font-size: 18px;
          line-height: 20px;
        }
      }
    }

    &__description {
      font-weight: 400;
      font-size: 8px;
      line-height: 12px;
      color: rgba(255, 255, 255, 0.7);

      @media (min-width: 768px) {
        font-size: 10px;
        line-height: 16px;
      }

      @media (min-width: 1024px) {
        font-size: 12px;
        line-height: 16px;
      }
    }
  }
}

@keyframes participate-hover {
  0% {
    bottom: 64px;
  }
  50% {
    bottom: -4px;
  }
  100% {
    bottom: 0px;
  }
}

@keyframes timer-hover {
  0% {
    bottom: -64px;
  }
  50% {
    bottom: 4px;
  }
  100% {
    bottom: 0;
  }
}

@keyframes soon {
  0% {
    background: rgba(255, 255, 255, 0.07);
  }

  50% {
    background: rgba(255, 255, 255, 0.001);
  }

  100% {
    background: rgba(255, 255, 255, 0.07);
  }
}
</style>
